import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Sparkles, Flower2, Leaf, ShoppingBag,
  Heart, ChevronRight, AlertCircle, CheckCircle2, Loader2, Star,
  BookOpen, Package
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";
import ImmersiveLayout from "../components/ImmersiveLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Step Config
// ============================================================
const STEPS_ID = [
  {
    key: "story",
    title: "Ceritakan kepada kami",
    subtitle: "Siapa dia, apa momennya, bagaimana perasaanmu?",
    type: "textarea",
    placeholder: "Contoh: Ibuku berulang tahun minggu depan. Dia selalu ada untukku dalam setiap momen terpenting hidupku...",
  },
  {
    key: "recipient_type",
    title: "Untuk siapa bouquet ini?",
    subtitle: "Pilih hubunganmu dengan penerima",
    type: "choice",
    options: [
      { value: "mother", label: "Ibu", icon: "💐" },
      { value: "partner", label: "Pasangan", icon: "💑" },
      { value: "friend", label: "Sahabat", icon: "🤝" },
      { value: "colleague", label: "Rekan Kerja", icon: "🌸" },
      { value: "self", label: "Untuk Diri Sendiri", icon: "🌟" },
      { value: "other", label: "Lainnya", icon: "✨" },
    ],
  },
  {
    key: "memory_depth",
    title: "Seberapa dalam kenangannya?",
    subtitle: "Ini membantu kami menentukan intensitas cerita di bouquet",
    type: "choice",
    options: [
      { value: "light", label: "Ringan & Ceria", icon: "☀️", desc: "Momen bahagia, semangat baru" },
      { value: "moderate", label: "Penuh Makna", icon: "🌿", desc: "Kenangan indah, rasa syukur" },
      { value: "deep", label: "Mendalam & Haru", icon: "🌊", desc: "Momen penting, emosi kuat" },
    ],
  },
  {
    key: "mood",
    title: "Suasana hati yang ingin disampaikan",
    subtitle: "Pilih nuansa yang paling cocok",
    type: "choice",
    options: [
      { value: "romantic", label: "Romantis", icon: "🌹" },
      { value: "soft", label: "Lembut & Feminin", icon: "🌸" },
      { value: "fresh", label: "Segar & Ceria", icon: "🌻" },
      { value: "elegant", label: "Elegan & Mewah", icon: "💎" },
      { value: "natural", label: "Natural & Hangat", icon: "🍃" },
      { value: "dramatic", label: "Dramatis & Berani", icon: "🔥" },
    ],
  },
  {
    key: "budget",
    title: "Kisaran budget",
    subtitle: "Kami akan menyesuaikan rangkaian terbaik untuk kamu",
    type: "choice",
    options: [
      { value: "small", label: "Rp 150rb – 250rb", icon: "💚", desc: "Mini bouquet" },
      { value: "medium", label: "Rp 250rb – 450rb", icon: "💛", desc: "Standard bouquet" },
      { value: "large", label: "Rp 450rb – 750rb", icon: "🧡", desc: "Premium bouquet" },
      { value: "premium", label: "Rp 750rb+", icon: "❤️", desc: "Luxury & eksklusif" },
    ],
  },
  {
    key: "flower_type",
    title: "Preferensi jenis bunga",
    subtitle: "Tidak wajib — bisa biarkan kami pilihkan",
    type: "choice",
    options: [
      { value: "fresh", label: "Bunga Segar", icon: "🌷" },
      { value: "dried", label: "Bunga Kering", icon: "🍂" },
      { value: "mix", label: "Campuran Segar & Kering", icon: "🌿" },
      { value: "ai_choose", label: "Serahkan pada Seikat", icon: "✨" },
    ],
  },
];

const STEPS_EN = [
  {
    key: "story",
    title: "Tell us your story",
    subtitle: "Who is it for, what's the occasion, how do you feel?",
    type: "textarea",
    placeholder: "Example: My mother's birthday is next week. She's always been there for me in every important moment...",
  },
  {
    key: "recipient_type",
    title: "Who is this bouquet for?",
    subtitle: "Select your relationship with the recipient",
    type: "choice",
    options: [
      { value: "mother", label: "Mother", icon: "💐" },
      { value: "partner", label: "Partner", icon: "💑" },
      { value: "friend", label: "Friend", icon: "🤝" },
      { value: "colleague", label: "Colleague", icon: "🌸" },
      { value: "self", label: "For Myself", icon: "🌟" },
      { value: "other", label: "Other", icon: "✨" },
    ],
  },
  {
    key: "memory_depth",
    title: "How deep is the memory?",
    subtitle: "This helps us shape the emotional intensity of your bouquet",
    type: "choice",
    options: [
      { value: "light", label: "Light & Joyful", icon: "☀️", desc: "Bright moments, fresh energy" },
      { value: "moderate", label: "Meaningful", icon: "🌿", desc: "Cherished memories, gratitude" },
      { value: "deep", label: "Deep & Touching", icon: "🌊", desc: "Significant moments, strong emotions" },
    ],
  },
  {
    key: "mood",
    title: "Mood to convey",
    subtitle: "Choose the vibe that feels right",
    type: "choice",
    options: [
      { value: "romantic", label: "Romantic", icon: "🌹" },
      { value: "soft", label: "Soft & Feminine", icon: "🌸" },
      { value: "fresh", label: "Fresh & Cheerful", icon: "🌻" },
      { value: "elegant", label: "Elegant & Luxe", icon: "💎" },
      { value: "natural", label: "Natural & Warm", icon: "🍃" },
      { value: "dramatic", label: "Dramatic & Bold", icon: "🔥" },
    ],
  },
  {
    key: "budget",
    title: "Budget range",
    subtitle: "We'll craft the best arrangement for you",
    type: "choice",
    options: [
      { value: "small", label: "Rp 150k – 250k", icon: "💚", desc: "Mini bouquet" },
      { value: "medium", label: "Rp 250k – 450k", icon: "💛", desc: "Standard bouquet" },
      { value: "large", label: "Rp 450k – 750k", icon: "🧡", desc: "Premium bouquet" },
      { value: "premium", label: "Rp 750k+", icon: "❤️", desc: "Luxury & exclusive" },
    ],
  },
  {
    key: "flower_type",
    title: "Flower type preference",
    subtitle: "Optional — we can choose for you",
    type: "choice",
    options: [
      { value: "fresh", label: "Fresh Flowers", icon: "🌷" },
      { value: "dried", label: "Dried Flowers", icon: "🍂" },
      { value: "mix", label: "Fresh & Dried Mix", icon: "🌿" },
      { value: "ai_choose", label: "Let Seikat Choose", icon: "✨" },
    ],
  },
];

// ============================================================
// Flower Card Component
// ============================================================
function FlowerCard({ flower, index, lang }) {
  const [expanded, setExpanded] = useState(false);
  const pastelColors = [
    "from-rose-950/60 to-pink-950/40",
    "from-purple-950/60 to-violet-950/40",
    "from-emerald-950/60 to-teal-950/40",
    "from-amber-950/60 to-yellow-950/40",
    "from-sky-950/60 to-blue-950/40",
  ];
  const bg = pastelColors[index % pastelColors.length];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.12 }}
      className={`relative rounded-2xl border border-white/10 bg-gradient-to-br ${bg} backdrop-blur-sm overflow-hidden`}
      data-testid={`flower-card-${index}`}
    >
      {flower.in_inventory && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30">
          <CheckCircle2 size={10} className="text-emerald-400" />
          <span className="text-[9px] font-medium text-emerald-400 uppercase tracking-wider">
            {lang === "id" ? "Tersedia" : "In Stock"}
          </span>
        </div>
      )}

      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
            <Flower2 size={18} className="text-seikat-pink" />
          </div>
          <div>
            <h4 className="font-heading text-base text-seikat-bone leading-tight">{flower.name}</h4>
            {flower.color_suggestion && (
              <p className="text-[11px] text-seikat-bone-muted mt-0.5 capitalize">{flower.color_suggestion}</p>
            )}
          </div>
        </div>

        {/* Meaning */}
        <p className="text-sm text-seikat-bone/80 leading-relaxed mb-2">{flower.meaning}</p>

        {/* Why chosen */}
        {flower.why_chosen && (
          <div className="flex items-start gap-2 mb-3">
            <Heart size={11} className="text-seikat-pink flex-shrink-0 mt-0.5" />
            <p className="text-xs text-seikat-pink/80 italic leading-relaxed">{flower.why_chosen}</p>
          </div>
        )}

        {/* Philosophy toggle */}
        {flower.philosophy && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-[11px] text-seikat-bone-muted hover:text-seikat-bone transition-colors"
            >
              <BookOpen size={11} />
              <span>{lang === "id" ? "Makna & Filosofi" : "Meaning & Philosophy"}</span>
              <ChevronRight
                size={11}
                className={`transition-transform ${expanded ? "rotate-90" : ""}`}
              />
            </button>
            <AnimatePresence>
              {expanded && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-xs text-seikat-bone-muted/80 leading-relaxed mt-2 pl-3 border-l border-white/10 italic"
                >
                  {flower.philosophy}
                </motion.p>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================
// Result Screen Component
// ============================================================
function ResultScreen({ result, answers, lang, onAddToCart, addingToCart, addedToCart, onGoToCart }) {
  const { bouquet_dna: dna, estimated_price } = result;
  const t = {
    yourBouquet: lang === "id" ? "Bouquet Untukmu" : "Your Bouquet",
    poemFor: lang === "id" ? "Pesan untuk penerima" : "A message for the recipient",
    curatorNote: lang === "id" ? "Catatan dari kurator" : "Curator's note",
    palette: lang === "id" ? "Palet warna" : "Color palette",
    estimatedPrice: lang === "id" ? "Estimasi harga" : "Estimated price",
    flowers: lang === "id" ? "Bunga dalam rangkaian ini" : "Flowers in this bouquet",
    orderBtn: lang === "id" ? "Pesan Bouquet Ini" : "Order This Bouquet",
    addedBtn: lang === "id" ? "Ditambahkan ke Keranjang!" : "Added to Cart!",
    addingBtn: lang === "id" ? "Menyimpan..." : "Saving...",
    goToCart: lang === "id" ? "Lihat Keranjang & Checkout" : "View Cart & Checkout",
    emotions: lang === "id" ? "Emosi yang terasa" : "Emotions detected",
  };

  return (
    <div className="min-h-screen pb-40" data-testid="result-screen">
      {/* Hero poem section */}
      <div className="relative min-h-[45vh] flex items-center justify-center px-6 py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-seikat-ink via-seikat-ink/95 to-transparent pointer-events-none" />
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-seikat-pink/10 border border-seikat-pink/20 mb-6"
          >
            <Sparkles size={13} className="text-seikat-pink" />
            <span className="text-xs font-medium text-seikat-pink tracking-wider uppercase">
              {dna?.bouquet_name || t.yourBouquet}
            </span>
          </motion.div>

          {/* Personal poem */}
          <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-heading text-xl md:text-2xl text-seikat-bone/95 leading-relaxed italic mb-6"
          >
            &ldquo;{dna?.personalized_poem}&rdquo;
          </motion.blockquote>

          {/* Curator note */}
          {dna?.curator_message && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="text-sm text-seikat-bone-muted leading-relaxed max-w-lg mx-auto"
            >
              — {dna.curator_message}
            </motion.p>
          )}

          {/* Emotions chips */}
          {dna?.emotions?.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-2 mt-6"
            >
              {dna.emotions.map((e, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-seikat-bone-muted capitalize">
                  {e}
                </span>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Details section */}
      <div className="max-w-3xl mx-auto px-4 space-y-6">
        {/* Price + palette row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-2 gap-4"
        >
          <div className="rounded-2xl border border-white/10 bg-white/3 p-5">
            <p className="text-xs text-seikat-bone-muted uppercase tracking-wider mb-2">{t.estimatedPrice}</p>
            <p className="font-heading text-lg text-seikat-bone">
              {dna?.estimated_price_range || `Rp ${(estimated_price || 350000).toLocaleString("id-ID")}`}
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/3 p-5">
            <p className="text-xs text-seikat-bone-muted uppercase tracking-wider mb-3">{t.palette}</p>
            <div className="flex flex-wrap gap-2">
              {(dna?.color_palette || []).map((c, i) => (
                <span key={i} className="text-xs text-seikat-bone/70 bg-white/5 px-2 py-0.5 rounded-full capitalize">{c}</span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Flower cards */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}>
          <h3 className="text-xs font-medium text-seikat-bone-muted uppercase tracking-wider mb-4 flex items-center gap-2">
            <Leaf size={13} />
            {t.flowers}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(dna?.recommended_flowers || []).map((flower, i) => (
              <FlowerCard key={i} flower={flower} index={i} lang={lang} />
            ))}
          </div>
        </motion.div>

        {/* CTA: Order button */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="sticky bottom-24 z-30"
        >
          {addedToCart ? (
            <button
              onClick={onGoToCart}
              data-testid="go-to-cart-btn"
              className="w-full py-4 rounded-2xl bg-seikat-lime text-seikat-ink font-semibold text-base flex items-center justify-center gap-3 hover:brightness-110 transition-all"
            >
              <ShoppingBag size={18} />
              {t.goToCart}
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={onAddToCart}
              disabled={addingToCart}
              data-testid="add-to-cart-btn"
              className="w-full py-4 rounded-2xl bg-seikat-pink text-white font-semibold text-base flex items-center justify-center gap-3 hover:brightness-110 transition-all disabled:opacity-60"
            >
              {addingToCart ? (
                <><Loader2 size={18} className="animate-spin" />{t.addingBtn}</>
              ) : (
                <><Package size={18} />{t.orderBtn}<ChevronRight size={16} /></>
              )}
            </button>
          )}
        </motion.div>

        <div className="h-8" />
      </div>
    </div>
  );
}

// ============================================================
// Login Prompt Overlay
// ============================================================
function LoginPromptOverlay({ lang, onLogin, onDismiss }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-seikat-ink/90 backdrop-blur-sm px-6"
      data-testid="login-prompt-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-seikat-charcoal border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center"
      >
        <div className="w-14 h-14 rounded-2xl bg-seikat-pink/10 border border-seikat-pink/20 flex items-center justify-center mx-auto mb-5">
          <Heart size={22} className="text-seikat-pink" />
        </div>
        <h3 className="font-heading text-xl text-seikat-bone mb-3">
          {lang === "id" ? "Masuk untuk melanjutkan" : "Sign in to continue"}
        </h3>
        <p className="text-sm text-seikat-bone-muted leading-relaxed mb-7">
          {lang === "id"
            ? "Ceritamu tersimpan aman. Masuk sebentar agar kami bisa menyimpan rangkaianmu dan membantumu checkout."
            : "Your story is safe. Sign in so we can save your bouquet and help you check out."}
        </p>
        <button
          onClick={onLogin}
          data-testid="login-prompt-btn"
          className="w-full py-3.5 rounded-2xl bg-seikat-pink text-white font-semibold mb-3 hover:brightness-110 transition-all"
        >
          {lang === "id" ? "Masuk / Daftar" : "Sign In / Sign Up"}
        </button>
        <button onClick={onDismiss} className="text-xs text-seikat-bone-muted hover:text-seikat-bone transition-colors">
          {lang === "id" ? "Kembali ke wizard" : "Back to wizard"}
        </button>
      </motion.div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================
export default function MemoryBouquetFlow() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { openCartDrawer, fetchCart } = useCart();
  const lang = language || "id";
  const STEPS = lang === "id" ? STEPS_ID : STEPS_EN;

  const [stepIndex, setStepIndex] = useState(0);
  const [stepHistory, setStepHistory] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const textareaRef = useRef(null);

  const currentStep = STEPS[stepIndex];
  const totalSteps = STEPS.length;
  const progress = Math.round(((stepIndex) / totalSteps) * 100);

  const t = {
    title: lang === "id" ? "Memory Bouquet" : "Memory Bouquet",
    step: lang === "id" ? "Langkah" : "Step",
    of: lang === "id" ? "dari" : "of",
    next: lang === "id" ? "Lanjut" : "Next",
    weave: lang === "id" ? "Rangkai Bouquet-ku" : "Weave My Bouquet",
    back: lang === "id" ? "Kembali" : "Back",
    interpreting: lang === "id" ? "Sedang merangkai ceritamu..." : "Weaving your story...",
    interpretingHint: lang === "id" ? "AI kami membaca emosi dan memilih bunga yang paling cocok" : "Our AI reads your emotions and selects the perfect flowers",
    errorTitle: lang === "id" ? "Terjadi kesalahan" : "Something went wrong",
    retry: lang === "id" ? "Coba Lagi" : "Try Again",
  };

  // Navigate to next step (with history tracking)
  const goNext = (selectedValue) => {
    const newAnswers = { ...answers, [currentStep.key]: selectedValue };
    setAnswers(newAnswers);
    setStepHistory([...stepHistory, stepIndex]);

    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Last step — weave
      handleWeave(newAnswers);
    }
  };

  // Navigate back one step
  const goBack = () => {
    if (stepHistory.length === 0) {
      navigate("/memory-bouquet");
      return;
    }
    const prev = stepHistory[stepHistory.length - 1];
    setStepIndex(prev);
    setStepHistory(stepHistory.slice(0, -1));
    setError(null);
  };

  const handleTextareaNext = () => {
    const val = (answers.story || "").trim();
    if (!val || val.length < 20) {
      setError(lang === "id" ? "Ceritakan sedikit lebih banyak (minimal 20 karakter)" : "Please share a bit more (min 20 characters)");
      return;
    }
    setError(null);
    goNext(val);
  };

  const handleWeave = async (finalAnswers) => {
    // Check login
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/bouquet/interpret`, {
        story_text: finalAnswers.story || "",
        recipient_type: finalAnswers.recipient_type || "",
        memory_depth: finalAnswers.memory_depth || "moderate",
        mood: finalAnswers.mood || "soft",
        budget: finalAnswers.budget || "medium",
        size: finalAnswers.size || "medium",
        flower_type: finalAnswers.flower_type || "fresh",
        language: lang,
      }, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setShowLoginPrompt(true);
      } else {
        setError(err.response?.data?.detail || (lang === "id" ? "Gagal merangkai. Coba lagi." : "Failed to weave. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!result?.request_id) return;
    setAddingToCart(true);
    try {
      await axios.post(`${API}/bouquet/${result.request_id}/add-to-cart`, {}, { withCredentials: true });
      await fetchCart();
      setAddedToCart(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setShowLoginPrompt(true);
      } else {
        setError(err.response?.data?.detail || (lang === "id" ? "Gagal menambahkan ke keranjang" : "Failed to add to cart"));
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const handleGoToCart = () => {
    openCartDrawer();
  };

  const handleLoginRedirect = () => {
    sessionStorage.setItem("mb_answers", JSON.stringify(answers));
    sessionStorage.setItem("mb_stepIndex", String(stepIndex));
    navigate("/login?redirect=/memory-bouquet/create");
  };

  // Restore answers from session if returning from login OR from landing chat
  useEffect(() => {
    // If coming from landing page Memory AI Chat — use chat story as pre-fill
    const chatStory = sessionStorage.getItem("mb_chat_story");
    if (chatStory && !answers.story) {
      setAnswers(prev => ({ ...prev, story: chatStory }));
      sessionStorage.removeItem("mb_chat_story");
    }

    // If returning from login redirect — restore full wizard state
    const saved = sessionStorage.getItem("mb_answers");
    const savedStep = sessionStorage.getItem("mb_stepIndex");
    if (saved && user) {
      try {
        const parsed = JSON.parse(saved);
        setAnswers(parsed);
        const idx = parseInt(savedStep || "0", 10);
        setStepIndex(idx);
        sessionStorage.removeItem("mb_answers");
        sessionStorage.removeItem("mb_stepIndex");
        // If at last step, auto-weave
        if (idx >= STEPS.length - 1) {
          handleWeave(parsed);
        }
      } catch {}
    }
  // eslint-disable-next-line
  }, [user]);

  // ── Loading State ──────────────────────────────────────────
  if (loading) {
    return (
      <ImmersiveLayout showAudio={false}>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="relative w-20 h-20 mx-auto mb-8">
              <div className="absolute inset-0 rounded-full border-2 border-seikat-pink/20 animate-ping" />
              <div className="w-20 h-20 rounded-full bg-seikat-pink/10 border border-seikat-pink/30 flex items-center justify-center">
                <Sparkles size={28} className="text-seikat-pink animate-pulse" />
              </div>
            </div>
            <h3 className="font-heading text-2xl text-seikat-bone mb-3">{t.interpreting}</h3>
            <p className="text-sm text-seikat-bone-muted max-w-xs">{t.interpretingHint}</p>
            <div className="flex justify-center gap-1.5 mt-8">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-2 h-2 rounded-full bg-seikat-pink/50 animate-bounce"
                  style={{ animationDelay: `${i * 0.2}s` }} />
              ))}
            </div>
          </motion.div>
        </div>
      </ImmersiveLayout>
    );
  }

  // ── Result State ──────────────────────────────────────────
  if (result) {
    return (
      <ImmersiveLayout showAudio={false}>
        {showLoginPrompt && (
          <LoginPromptOverlay
            lang={lang}
            onLogin={handleLoginRedirect}
            onDismiss={() => setShowLoginPrompt(false)}
          />
        )}
        <ResultScreen
          result={result}
          answers={answers}
          lang={lang}
          onAddToCart={handleAddToCart}
          addingToCart={addingToCart}
          addedToCart={addedToCart}
          onGoToCart={handleGoToCart}
        />
      </ImmersiveLayout>
    );
  }

  // ── Wizard State ──────────────────────────────────────────
  return (
    <ImmersiveLayout showAudio={false}>
      {showLoginPrompt && (
        <LoginPromptOverlay
          lang={lang}
          onLogin={handleLoginRedirect}
          onDismiss={() => setShowLoginPrompt(false)}
        />
      )}

      <div className="min-h-screen flex flex-col pb-36 pt-20">
        {/* Progress bar */}
        <div className="fixed top-0 left-0 right-0 z-40 h-1 bg-white/5">
          <motion.div
            className="h-full bg-gradient-to-r from-seikat-pink to-seikat-lime"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>

        {/* Step counter */}
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-40">
          <span className="text-xs font-mono text-seikat-bone-muted tracking-widest">
            {String(stepIndex + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
          </span>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-4 max-w-2xl mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={stepIndex}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
            >
              {/* Step header */}
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl md:text-3xl text-seikat-bone mb-2">
                  {currentStep.title}
                </h2>
                <p className="text-sm text-seikat-bone-muted">{currentStep.subtitle}</p>
              </div>

              {/* Textarea step */}
              {currentStep.type === "textarea" && (
                <div className="space-y-4">
                  <textarea
                    ref={textareaRef}
                    value={answers.story || ""}
                    onChange={e => setAnswers({ ...answers, story: e.target.value })}
                    placeholder={currentStep.placeholder}
                    rows={5}
                    data-testid="mb-story-input"
                    className="w-full rounded-2xl bg-white/5 border border-white/10 focus:border-seikat-pink/50 text-seikat-bone placeholder-seikat-bone-muted/40 px-5 py-4 text-sm leading-relaxed resize-none outline-none transition-colors"
                    onKeyDown={e => {
                      if (e.key === "Enter" && e.metaKey) handleTextareaNext();
                    }}
                  />
                  {error && (
                    <div className="flex items-center gap-2 text-amber-400 text-xs">
                      <AlertCircle size={13} />
                      <span>{error}</span>
                    </div>
                  )}
                  <button
                    onClick={handleTextareaNext}
                    data-testid="mb-story-send"
                    className="w-full py-4 rounded-2xl bg-seikat-pink text-white font-medium text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all"
                  >
                    {t.next} <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* Choice step */}
              {currentStep.type === "choice" && (
                <div className="grid grid-cols-2 gap-3">
                  {currentStep.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => goNext(opt.value)}
                      data-testid={`mb-step-${currentStep.key}-${opt.value}`}
                      className={`group relative rounded-2xl border p-4 text-left transition-all hover:border-seikat-pink/50 hover:bg-seikat-pink/5
                        ${answers[currentStep.key] === opt.value
                          ? "border-seikat-pink/60 bg-seikat-pink/10"
                          : "border-white/10 bg-white/3"
                        }`}
                    >
                      <span className="text-2xl block mb-2">{opt.icon}</span>
                      <span className="block text-sm font-medium text-seikat-bone">{opt.label}</span>
                      {opt.desc && (
                        <span className="block text-xs text-seikat-bone-muted mt-0.5">{opt.desc}</span>
                      )}
                      {answers[currentStep.key] === opt.value && (
                        <div className="absolute top-2.5 right-2.5">
                          <CheckCircle2 size={14} className="text-seikat-pink" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Error */}
              {error && currentStep.type !== "textarea" && (
                <div className="mt-4 flex items-center gap-2 text-amber-400 text-xs justify-center">
                  <AlertCircle size={13} />
                  <span>{error}</span>
                  <button onClick={() => { setError(null); handleWeave(answers); }}
                    className="underline hover:no-underline">{t.retry}</button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom nav — back button */}
        <div className="fixed bottom-24 left-0 right-0 flex justify-center px-4 z-30 pointer-events-none">
          <div className="pointer-events-auto">
            <button
              onClick={goBack}
              data-testid="mb-back-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-dark text-seikat-bone-muted hover:text-seikat-bone text-sm transition-colors"
            >
              <ArrowLeft size={14} /> {t.back}
            </button>
          </div>
        </div>
      </div>
    </ImmersiveLayout>
  );
}
